/**
 * Small branded in-page loader (Part C of the loading system; markup in snippets/igloo-spinner.liquid,
 * styles in snippets/igloo-loader.liquid).
 *
 * Watches the theme's own requests and shows the igloo spinner while they are in flight, without changing
 * how any feature works (every request is passed through untouched):
 *  - /cart/add            → spinner in the add to cart button that was used (buy box, card quick-add).
 *                           The sticky bar has its own "Adding…" state, so its puppet clicks are skipped.
 *  - /cart/change|update  → spinner beside the quantity stepper of the cart line being changed, or in the
 *                           discount button. Cart note saves are ignored.
 *  - /products/… from a quick-add "Choose" button → spinner in that button while the options panel loads.
 *  - Section re-renders of /collections/… and /search (filtering, sorting, pagination) → soft white
 *    overlay over the product grid, only if the update takes longer than 300ms.
 *  - /search/suggest      → spinner in place of the predictive search magnifier.
 */

const SHOW_DELAY = 150; // avoid flashing a spinner for instant responses
const GRID_DELAY = 300;
const INTERACTION_WINDOW = 4000;

/** @type {{ el: Element, time: number } | null} */
let lastInteraction = null;

const remember = (/** @type {Event} */ event) => {
  if (event.target instanceof Element) lastInteraction = { el: event.target, time: performance.now() };
};
for (const type of ['pointerdown', 'click', 'keydown', 'change', 'submit']) {
  document.addEventListener(type, remember, true);
}

/** @returns {Element | null} */
function recentSource() {
  if (!lastInteraction || performance.now() - lastInteraction.time > INTERACTION_WINDOW) return null;
  return lastInteraction.el.isConnected ? lastInteraction.el : null;
}

function spinner() {
  const template = /** @type {HTMLTemplateElement | null} */ (document.getElementById('IglooSpinnerTemplate'));
  const node = template?.content.firstElementChild?.cloneNode(true);
  return node instanceof HTMLElement ? node : null;
}

/**
 * Runs `start` after `delay` unless the request finished first; returns the end handler.
 * @param {number} delay @param {() => (() => void) | void} start
 */
function delayed(delay, start) {
  let stop = /** @type {(() => void) | void} */ (undefined);
  const timer = setTimeout(() => {
    stop = start();
  }, delay);
  return () => {
    clearTimeout(timer);
    if (stop) stop();
  };
}

/** Spinner covering a button's content. @param {HTMLElement} button @param {{ keepAdded?: boolean }} [options] */
function busyButton(button, options = {}) {
  const formId = button instanceof HTMLButtonElement ? button.form?.id : undefined;
  return delayed(SHOW_DELAY, () => {
    const layer = document.createElement('span');
    layer.className = 'igloo-busy-layer';
    const icon = spinner();
    if (icon) layer.append(icon);
    const setPosition = getComputedStyle(button).position === 'static';
    if (setPosition) button.style.position = 'relative';
    button.dataset.iglooBusy = 'true';
    button.append(layer);
    return () => {
      layer.remove();
      delete button.dataset.iglooBusy;
      if (setPosition) button.style.removeProperty('position');
      // Add to cart: make sure the theme's "Added ✓" state is still seen after a slow request
      // (the theme may have re-rendered the button meanwhile, so use the current one)
      const current = button.isConnected
        ? button
        : (button.id && document.getElementById(button.id)) ||
          (formId && document.getElementById(formId)?.querySelector('button[name="add"]'));
      if (options.keepAdded && current instanceof HTMLElement) {
        // Hold "Added ✓" for 1.2s even if the theme's own reset timer (started at the click) fires meanwhile
        current.dataset.added = 'true';
        const hold = new MutationObserver(() => {
          if (current.dataset.added !== 'true') current.dataset.added = 'true';
        });
        hold.observe(current, { attributes: true, attributeFilter: ['data-added'] });
        setTimeout(() => {
          hold.disconnect();
          current.removeAttribute('data-added');
        }, 1200);
      }
    };
  });
}

/** Spinner beside a cart line's quantity stepper. @param {Element} row */
function busyCartLine(row) {
  return delayed(SHOW_DELAY, () => {
    const target = row.querySelector('.cart-items__quantity-controls') || row.querySelector('.cart-items__quantity');
    const icon = spinner();
    if (!target || !icon) return;
    const wrap = document.createElement('span');
    wrap.className = 'igloo-cart-line-spinner';
    wrap.append(icon);
    target.append(wrap);
    return () => wrap.remove();
  });
}

/** Soft overlay over the product grid. */
function busyGrid() {
  return delayed(GRID_DELAY, () => {
    const grid = document.querySelector('results-list .product-grid, .product-grid');
    const host = grid?.parentElement;
    const icon = spinner();
    if (!grid || !host || !icon) return;
    const overlay = document.createElement('div');
    overlay.className = 'igloo-grid-overlay';
    overlay.append(icon);
    const setPosition = getComputedStyle(host).position === 'static';
    if (setPosition) host.style.position = 'relative';
    host.append(overlay);
    return () => {
      overlay.remove();
      if (setPosition && host.isConnected) host.style.removeProperty('position');
    };
  });
}

/** Spinner in place of the predictive search magnifier. */
function busySearch() {
  return delayed(SHOW_DELAY, () => {
    const component = document.querySelector('predictive-search-component');
    const iconWrap = component?.querySelector('.predictive-search__icon');
    const icon = spinner();
    if (!component || !iconWrap || !icon) return;
    iconWrap.append(icon);
    component.classList.add('igloo-search-busy');
    return () => {
      icon.remove();
      component.classList.remove('igloo-search-busy');
    };
  });
}

let pendingSearch = 0;
let endSearch = /** @type {(() => void) | null} */ (null);

/**
 * Decides which loader (if any) a request gets. Returns its end handler.
 * @param {URL} url
 * @returns {(() => void) | null}
 */
function loaderFor(url) {
  const path = url.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, ''); // strip a locale prefix
  const source = recentSource();

  if (/\/cart\/add(\.js)?$/.test(path)) {
    const button = source?.closest('button[name="add"], .add-to-cart-button');
    if (!(button instanceof HTMLElement) || button.dataset.puppet === 'true') return null;
    return busyButton(button, { keepAdded: button.classList.contains('add-to-cart-button') });
  }

  if (/\/cart\/(change|update)(\.js)?$/.test(path)) {
    if (!source || source.closest('cart-note, .cart-note')) return null;
    const discount = source.closest('cart-discount-component');
    if (discount) {
      const button = source.closest('button') || discount.querySelector('button[type="submit"]');
      return button instanceof HTMLElement ? busyButton(button) : null;
    }
    const row = source.closest('.cart-items__table-row');
    return row ? busyCartLine(row) : null;
  }

  if (path.startsWith('/search/suggest')) {
    // Predictive search aborts and restarts requests while typing: keep one spinner for the whole burst
    pendingSearch++;
    if (!endSearch) endSearch = busySearch();
    return () => {
      pendingSearch--;
      if (pendingSearch <= 0 && endSearch) {
        endSearch();
        endSearch = null;
        pendingSearch = 0;
      }
    };
  }

  if (url.searchParams.has('section_id') && (path.startsWith('/collections') || path === '/search')) {
    return busyGrid();
  }

  if (path.startsWith('/products/')) {
    const choose = source?.closest('.quick-add__button--choose');
    if (choose instanceof HTMLElement) return busyButton(choose);
  }

  return null;
}

const nativeFetch = window.fetch;

window.fetch = function (input, init) {
  const request = nativeFetch.apply(this, /** @type {any} */ (arguments));
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input instanceof Request ? input.url : String(input);
    const url = new URL(raw, window.location.href);
    if (url.origin !== window.location.origin) return request;
    const end = loaderFor(url);
    if (end) {
      // Finish once the body has arrived and the theme has had a frame to render it
      request
        .then((response) => response.clone().text())
        .catch(() => {})
        .then(() => requestAnimationFrame(() => end()));
    }
  } catch (error) {
    // Never let the loader affect the request
  }
  return request;
};
